import { Observable, map, tap } from "rxjs";
import {
    AuditLog,
    AuditLogDb,
    AuditLogHelper,
    AuditType,
    AuditTypeDb,
    AuditTypeHelper,
    IAuditLogFilterParams,
} from "../models/audit-log-type.model";

import { Injectable } from "@angular/core";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { DbCrudHelper, ErrorToSuppress } from "@app/shared/services/db-crud-helper";
import { FAIL_MESSAGE } from "./audit-log.constants";

@Injectable({
    providedIn: "root",
})
export class AuditLogDbService {
    static readonly createAuditMethod = "CreateAudit";
    static readonly updateAuditMethod = "UpdateAuditLog";
    private readonly auditLogHelper = new AuditLogHelper();
    private readonly auditTypeHelper = new AuditTypeHelper();
    private readonly PERMISSION_DENIED_ERROR = ErrorToSuppress.PERMISSION_DENIED_ERROR;

    constructor(private readonly dbCrudHelper: DbCrudHelper) {}
    logEvent(logData: AuditLog, isEdit = false): Observable<AuditLog> {
        if (isEdit) {
            return this.editAuditLog(logData).pipe(map((log: AuditLogDb) => this.auditLogHelper.fromDbObject(log)));
        }

        return this.addAuditLog(logData).pipe(map((log: AuditLogDb) => this.auditLogHelper.fromDbObject(log)));
    }

    getAuditLogTypes(): Observable<ClareDbCollection<AuditType> | null> {
        return this.requestOrUpdateAuditEventData<AuditTypeDb[]>("GetAllAuditTypes").pipe(
            map(auditTypes => auditTypes.map((c: AuditTypeDb) => this.auditTypeHelper.fromDbObject(c))),
            map(auditTypes =>
                auditTypes.reduce(
                    (collection, c) => ({
                        ...collection,
                        [c.id]: c,
                    }),
                    {}
                )
            )
        );
    }

    getAuditLogs(input: IAuditLogFilterParams): Observable<ClareDbCollection<AuditLog> | null> {
        return this.requestOrUpdateAuditEventData<AuditLogDb[]>("GetAudits", AuditLogHelper.getAuditLogFilterParams(input)).pipe(
            map(audits => audits.map((c: AuditLogDb) => this.auditLogHelper.fromDbObject(c)))
        );
    }

    getLogTypesByCategory(category: string): Observable<AuditType[]> {
        return this.getAuditLogTypes().pipe(
            map((auditTypeObj: ClareDbCollection<AuditType>) =>
                Object.values(auditTypeObj || {}).filter(auditType => auditType.category.toLowerCase() === category)
            )
        );
    }

    addAuditLog(logData: AuditLog) {
        const dbLogData = this.auditLogHelper.toDbObject(logData);
        return this.requestOrUpdateAuditEventData<AuditLogDb>(AuditLogDbService.createAuditMethod, dbLogData).pipe(tap(result => result));
    }

    editAuditLog(logData: AuditLog) {
        const dbLogData = this.auditLogHelper.toDbObject(logData);
        return this.requestOrUpdateAuditEventData<AuditLogDb>(AuditLogDbService.updateAuditMethod, dbLogData).pipe(tap(result => result));
    }

    private requestOrUpdateAuditEventData<T>(method: string, params: unknown = {}): Observable<T> {
        return this.dbCrudHelper.makeRequest<T>(method, params, null, {
            originalValue: this.PERMISSION_DENIED_ERROR,
            newValue: FAIL_MESSAGE,
        });
    }
}
